import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './index.css';
import 'react-datepicker/dist/react-datepicker.css'; // base; themed in theme.css
import 'leaflet/dist/leaflet.css'; // base; themed in theme.css
// Self-hosted variable fonts for the public marketing site (Fraunces display serif +
// Inter body). Bundled locally — no external font request, works offline / behind a proxy.
import '@fontsource-variable/fraunces';
import '@fontsource-variable/inter';
import './styles/theme.css'; // Tailwind v4 entry + Green Bay design tokens (loaded last)
import './lib/time'; // installs dayjs IST default — must be imported before any date rendering
import App from './App';
import { useThemeStore } from './store/theme';
import { Toaster } from './ui';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
  },
});

function ThemedApp() {
  const mode = useThemeStore((s) => s.mode);
  const resolved = useThemeStore((s) => s.resolved)();

  // Re-render when the OS scheme flips and the user has 'system' selected.
  useEffect(() => {
    if (mode !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => useThemeStore.setState({ mode: 'system' }); // forces resolved() to re-run
    mq.addEventListener?.('change', handler);
    return () => mq.removeEventListener?.('change', handler);
  }, [mode]);

  // Sync the html data-theme attribute so our own CSS can target it.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', resolved);
    // Light canvas is a clear soft sage so white cards read as cards (not a
    // sea of white); dark canvas stays near-black.
    document.body.style.background = resolved === 'dark' ? '#0e1210' : '#edf0ea';
  }, [resolved]);

  // The app is fully Tailwind now (Ant Design removed) — theming lives in styles/theme.css,
  // driven by the `data-theme` attribute synced by the effect above; no provider wrapper needed.
  return (
    <>
      <App />
      <Toaster />
    </>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <ThemedApp />
      </QueryClientProvider>
    </BrowserRouter>
  </React.StrictMode>,
);
